<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'petite-vue-i18n';
import { utils } from '../../utils';
import { useClassStore } from '../../stores';
import { AttendStatus, ClassGroupInfo, ClassStatus, ClassStudentInfo } from '../../types/class';
import { TEXT } from '../../configs/text';

import Heading from '../General/Heading.vue';
import Modal from '../General/Modal.vue';
import Button from '../General/Button.vue';
import goldMedalIcon from '../../assets/icons/medal_gold.svg'
import silverMedalIcon from '../../assets/icons/medal_silver.svg'
import bronzeMedalIcon from '../../assets/icons/medal_bronze.svg'
import cupIcon from '../../assets/icons/cup.svg'
import addStudentIcon from '../../assets/icons/add_student.svg';
import SetGroup from './SetGroup.vue';
import SetPointStudent from '../Control/SetPointStudent.vue';
import SetPointGroup from '../Control/SetPointGroup.vue';

const modalAddGroupShow = ref<boolean>(false)
const modalChangeGroupShow = ref<boolean>(false)
const modalSetPointStudentShow = ref<boolean>(false)
const modalSetPointGroupShow = ref<boolean>(false)
const selectedStudent = ref<ClassStudentInfo | null>(null)
const selectedGroup = ref<ClassGroupInfo | null>(null)

const classStore = useClassStore()
const studentAddGroupIds = computed<number[]>(() => {
    const studentIds = classStore.selectedClassDetails?.students?.filter(student => student.status === AttendStatus.attended && !student.group_id).map(student => student.id) || []
    return studentIds
})
const studentChangeGroupIds = ref<number[]>([])

function rankedGroupIcon(group: ClassGroupInfo): string {
    if (classStore.selectedClassDetails?.state !== ClassStatus.on_going) return '';
    const isTopGroup = classStore.rankedGroups.some(x => x.rank === 1 && x.id === group.id);
    return isTopGroup ? cupIcon : '';
}

function rankedStudentIcon(student: ClassStudentInfo): string {
    if (classStore.selectedClassDetails?.state !== ClassStatus.on_going) return '';
    const rankIconMap: { [key: number]: string } = {
        1: goldMedalIcon,
        2: silverMedalIcon,
        3: bronzeMedalIcon
    };
    const topStudent = classStore.rankedStudents.find(x => x.id === student.id && x.rank <= 3);
    return topStudent ? rankIconMap[topStudent.rank] : '';
}

function handleStudentClick(student: ClassStudentInfo) {
    if (classStore.selectedClassDetails?.state === ClassStatus.on_going) {
        // SetScore
        selectedStudent.value = student
        modalSetPointStudentShow.value = true
        return;
    }

    studentChangeGroupIds.value = []
    selectStudentChangeGroup(student.id)
    modalChangeGroupShow.value = true
}

function selectStudentChangeGroup(studentId: number) {
    const studentIndex = studentChangeGroupIds.value.indexOf(studentId)
    if (studentIndex >= 0) {
        // remove selected student
        studentChangeGroupIds.value.splice(studentIndex, 1)
        return
    }
    studentChangeGroupIds.value.push(studentId)
}

function handleGroupPointClick(group: ClassGroupInfo) {
    // SetScore
    selectedGroup.value = group
    modalSetPointGroupShow.value = true
}

const { t } = useI18n()
</script>

<template>
    <div class="flex flex-no-wrap gap-3 flex-1 overflow-auto pb-3">
        <div v-for="group in classStore.studentGroups" class="flex flex-col border-2 border-neutral-200/70 bg-neutral-100 rounded-lg p-3 min-w-[500px] w-full">
            <div class="flex justify-between mb-3">
                <div class="flex flex-col">
                    <Heading class="!mb-0">{{ group.name }}</Heading>
                    <p>{{ t(TEXT.students) }}: <strong>{{ group.students?.length || 0 }}</strong></p>
                </div>
                <img :src="addStudentIcon" class="cursor-pointer" @click="modalAddGroupShow = true" v-if="classStore.selectedClassDetails?.state === ClassStatus.not_started" />
                <div class="flex items-center gap-3" v-if="classStore.selectedClassDetails?.state === ClassStatus.on_going">
                    <img class="size-10" :src="rankedGroupIcon(group)" alt="Cup" v-if="rankedGroupIcon(group)">
                    <Button color="info" :shadow="false" @click="handleGroupPointClick(group)">
                        <span class="text-2xl">
                            {{ utils.calculateGroupTotalPoint(group) }}
                        </span>
                        <span class="font-normal">
                            {{ t(TEXT.points) }}
                        </span>
                    </Button>
                </div>
            </div>
            <div class="grid grid-cols-2 w-full gap-3 overflow-auto">
                <Button color="info" :shadow="false" v-for="student in group.students" @click="handleStudentClick(student)">
                    <div class="flex w-full flex-col items-center">
                        <div class="flex flex-col justify-around font-bold text-black leading-6 h-12 overflow-hidden my-auto">
                            {{ student.full_name }}
                        </div>
                        <div class="flex items-center gap-2">
                            <div class="size-2.5 rounded-full" :class="student.connected ? 'bg-lime-500' : 'bg-red-500'">
                            </div>
                            <div class="font-normal">{{ student.username }}</div>
                        </div>
                        <div class="flex w-full justify-center h-10 relative">
                            <img :src="rankedStudentIcon(student)" alt="Medal" class="size-10 absolute left-0" v-if="rankedStudentIcon(student)">
                            <div class="flex items-center justify-center text-2xl" v-if="classStore.selectedClassDetails?.state === ClassStatus.on_going">
                                {{ utils.calculateTotalPoint(student.point) }}
                            </div>
                        </div>
                    </div>
                </Button>
            </div>
        </div>
    </div>
    <Modal v-model="modalAddGroupShow" size="large">
        <Heading alignment="center">{{ t(TEXT.add_student_group) }}</Heading>
        <SetGroup :key="studentAddGroupIds.toString()" :student-ids="studentAddGroupIds" @update="modalAddGroupShow = false" />
    </Modal>
    <Modal v-model="modalChangeGroupShow" size="large">
        <Heading alignment="center">{{ t(TEXT.change_student_group) }}</Heading>
        <SetGroup :key="studentChangeGroupIds.toString()" :student-ids="studentChangeGroupIds" @update="modalChangeGroupShow = false" />
    </Modal>
    <Modal v-model="modalSetPointStudentShow" size="medium">
        <SetPointStudent v-if="selectedStudent !== null" :student="selectedStudent" @update="modalSetPointStudentShow = false" />
    </Modal>
    <Modal v-model="modalSetPointGroupShow" size="medium">
        <SetPointGroup v-if="selectedGroup !== null" :group="selectedGroup" @update="modalSetPointGroupShow = false" />
    </Modal>
</template>